import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import JsBarcode from "jsbarcode";
import {
  BsArrowLeft,
  BsBoxSeam,
  BsCloudUpload,
  BsUpcScan,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";
import ActivityLog from "../activity/ActivityLog";

import "./inventoryproductadd.css";
import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";
import { servicePostNewProduct } from "../../api/serviceApi";

const INITIAL_FORM = {
  mpn: "",
  shelfAddress: "",
  supplier: "",

  name: "",
  description: "",
  type: "",

  cost: "",
  wholesale: "",
  retail: "",

  quantity: "",
  minQuantity: "",
  maxQuantity: "",
};

const PRODUCT_TYPES = [
  "Air Fittings",
  "aluminium block",
  "Brackets",
  "Computer",
  "Display",
  "Documentation",
  "Electrical Cable",
  "Electrical Connectors",
  "Electronics",
  "Fasteners",
  "Other Fittings",
  "PCB",
  "Sensors",
  "Tools",
];

const NewProduct = () => {
  const navigate = useNavigate();
  const barcodeRef = useRef(null);

  const [form, setForm] = useState(INITIAL_FORM);

  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);

  //GST calculation
  const gst = useMemo(() => {
    const costExGst = Number(form.cost);

    if (!Number.isFinite(costExGst) || costExGst <= 0) {
      return 0;
    }

    return Number((costExGst * 0.1).toFixed(2));
  }, [form.cost]);

  //Cost including GST calculation
  const cost_inc_gst = useMemo(() => {
    const costExGst = Number(form.cost);

    if (!Number.isFinite(costExGst) || costExGst <= 0) {
      return 0;
    }

    return Number((costExGst + gst).toFixed(2));
  }, [form.cost, gst]);

  // image preview
  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(image);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  // MPN barcode
  useEffect(() => {
    if (!form.mpn || !barcodeRef.current) {
      return;
    }

    try {
      JsBarcode(barcodeRef.current, form.mpn, {
        format: "CODE128",
        lineColor: "#000000",
        width: 2,
        height: 52,
        displayValue: true,
        fontSize: 13,
        margin: 8,
      });
    } catch (error) {
      console.error("Barcode render error:", error);
    }
  }, [form.mpn]);

  const generateMPN = () => {
    const uniqueNumber = Math.floor(1000000 + Math.random() * 9000000);

    let generatedMpn = String(uniqueNumber);

    if (form.type && form.shelfAddress.trim()) {
      const typeInitials = form.type
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0]?.toLowerCase())
        .join("");

      generatedMpn = `${typeInitials}-${form.shelfAddress.trim()}-${uniqueNumber}`;
    }

    generatedMpn = generatedMpn.substring(0, 13);

    setForm((previous) => ({
      ...previous,
      mpn: generatedMpn,
    }));
  };

  // automatically regenerate MPN when type/shelf changes
  useEffect(() => {
    generateMPN();
  }, [form.type, form.shelfAddress]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // image choose
  const handleImageChoose = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image.");
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be 5MB or less.");
      event.target.value = "";
      return;
    }

    setImage(file);
  };

  // upload product image
  const uploadProductImage = async (mpn) => {
    if (!image) {
      return "";
    }

    // Use a unique image filename for new products.
    //
    // This prevents accidentally replacing an existing
    // product image if the new MPN is rejected as duplicate.
    const imageFilename = `${mpn}-${Date.now()}`;

    console.log("MPN:", mpn);
    console.log("imageFilename:", imageFilename);

    const result = await serviceUploadPhoto({
      file: image,
      folder: "tims/inventory",
      filename: imageFilename,
    });

    if (!result?.url) {
      throw new Error("Image upload failed");
    }

    return result.url;
  };

  const validateForm = () => {
    const mpn = form.mpn.trim();
    const name = form.name.trim();
    const cost = Number(form.cost);

    if (!mpn) {
      toast.error("MPN required.");
      return false;
    }

    if (!name) {
      toast.error("Product name required.");
      return false;
    }

    if (!Number.isFinite(cost) || cost <= 0) {
      toast.error("Product cost required and must be greater than 0.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!validateForm()) {
      return;
    }

    const mpn = form.mpn.trim();
    let uploadImageUrl = "";

    try {
      setSubmitting(true);

      // 1. Upload optional image
      uploadImageUrl = await uploadProductImage(mpn);

      // 2. build product payload
      const productData = {
        mpn,
        shelfAddress: form.shelfAddress.trim(),
        supplier: form.supplier.trim(),

        name: form.name.trim(),
        description: form.description.trim(),
        type: form.type,
        imageUrl: uploadImageUrl,

        cost: Number(form.cost) || 0,
        wholesale: Number(form.wholesale) || 0,
        retail: Number(form.retail) || 0,

        quantity: Number(form.quantity) || 0,
        minQuantity: Number(form.minQuantity) || 0,
        maxQuantity: Number(form.maxQuantity) || 0,
      };

      // 3. post create product
      await servicePostNewProduct(productData);

      ActivityLog("Product Added", `${mpn} MPN added`);

      toast.success("Product added successfully.");
      navigate("/inventory");
    } catch (error) {
      console.error("Error adding product:", error);
      // If image succeeded but creating the product failed,
      // clean up the orphan image.
      if (uploadImageUrl) {
        try {
          await serviceDeletePhoto(uploadImageUrl);
        } catch (deleteError) {
          console.error("Failed to clean up uploaded image:", deleteError);
        }
      }

      toast.error(error?.message || "Failed to add product.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="new-product-page">
      <Sidebar />

      <main className="new-product-content">
        <div className="new-product-container">
          <header className="new-product-header">
            <button
              type="button"
              className="new-product-back"
              onClick={() => navigate(-1)}
            >
              <BsArrowLeft />
              Back
            </button>

            <h1>Add Product</h1>
            <p>Create a new inventory product and configure stock levels.</p>
          </header>

          <form className="new-product-card" onSubmit={handleSubmit}>
            <aside className="new-product-sidebar">
              <div className="new-product-image-preview">
                {previewUrl ? (
                  <img src={previewUrl} alt="Product preview" />
                ) : (
                  <BsBoxSeam />
                )}
              </div>

              <div className="new-product-image-info">
                <strong>Product Image</strong>
                <span>Upload an optional image for this product.</span>
              </div>

              <label htmlFor="product-image" className="new-product-upload">
                <BsCloudUpload />
                {image ? "Change Image" : "Upload Image"}
              </label>

              <input
                id="product-image"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
                className="new-product-file"
                onChange={handleImageChoose}
                disabled={submitting}
              />

              {image && (
                <div className="new-product-selected-file">
                  <span>{image.name}</span>

                  <button
                    type="button"
                    onClick={() => {
                      setImage(null);
                    }}
                    disabled={submitting}
                  >
                    Remove
                  </button>
                </div>
              )}

              {submitting && image && (
                <div className="new-product-progress">
                  <div>
                    <span>Uploading image...</span>
                  </div>
                </div>
              )}

              <div className="new-product-barcode">
                <div className="new-product-barcode-title">
                  <BsUpcScan />
                  <strong>Product Barcode</strong>
                </div>

                <canvas ref={barcodeRef} />

                <button
                  type="button"
                  className="new-product-regenerate"
                  onClick={generateMPN}
                >
                  Regenerate MPN
                </button>
              </div>
            </aside>

            <div className="new-product-form">
              <section className="new-product-section">
                <div className="new-product-section-header">
                  <h2>Product Details</h2>
                  <p>Basic product and inventory identification.</p>
                </div>

                <div className="new-product-grid">
                  <div className="new-product-field">
                    <label htmlFor="name">
                      Name <span>*</span>
                    </label>

                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={form.name}
                      placeholder="Product name"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="type">Type</label>

                    <select
                      id="type"
                      name="type"
                      value={form.type}
                      onChange={handleInputChange}
                    >
                      <option value="">Select product type...</option>

                      {PRODUCT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="new-product-field new-product-full">
                    <label htmlFor="description">Description</label>

                    <textarea
                      id="description"
                      name="description"
                      value={form.description}
                      placeholder="Product description"
                      rows="3"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="shelfAddress">Shelf Address</label>

                    <input
                      id="shelfAddress"
                      type="text"
                      name="shelfAddress"
                      value={form.shelfAddress}
                      placeholder="e.g. A-12"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="mpn">
                      MPN <span>*</span>
                    </label>

                    <div className="new-product-mpn-control">
                      <input
                        id="mpn"
                        type="text"
                        name="mpn"
                        maxLength={13}
                        value={form.mpn}
                        placeholder="Manufacturer part number"
                        onChange={handleInputChange}
                        required
                      />

                      <button
                        type="button"
                        onClick={generateMPN}
                        title="Generate MPN"
                      >
                        <BsUpcScan />
                      </button>
                    </div>
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="supplier">Supplier</label>

                    <input
                      id="supplier"
                      type="text"
                      name="supplier"
                      value={form.supplier}
                      placeholder="Supplier"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </section>

              <div className="new-product-divider" />

              <section className="new-product-section">
                <div className="new-product-section-header">
                  <h2>Pricing</h2>
                  <p>Product cost, GST and selling prices.</p>
                </div>

                <div className="new-product-grid three-col">
                  <div className="new-product-field">
                    <label htmlFor="cost">
                      Cost Ex GST <span>*</span>
                    </label>

                    <input
                      id="cost"
                      type="number"
                      name="cost"
                      min="0"
                      step="0.01"
                      value={form.cost}
                      placeholder="0.00"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="gst">GST</label>

                    <input id="gst" type="number" value={gst} disabled />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="cost_inc_gst">Cost Inc GST</label>

                    <input
                      id="cost_inc_gst"
                      type="number"
                      value={cost_inc_gst}
                      disabled
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="wholesale">Wholesale</label>

                    <input
                      id="wholesale"
                      type="number"
                      name="wholesale"
                      min="0"
                      step="0.01"
                      value={form.wholesale}
                      placeholder="0.00"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="retail">Retail</label>

                    <input
                      id="retail"
                      type="number"
                      name="retail"
                      min="0"
                      step="0.01"
                      value={form.retail}
                      placeholder="0.00"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </section>

              <div className="new-product-divider" />

              <section className="new-product-section">
                <div className="new-product-section-header">
                  <h2>Stock Levels</h2>
                  <p>Current quantity and stock alert thresholds.</p>
                </div>

                <div className="new-product-grid three-col">
                  <div className="new-product-field">
                    <label htmlFor="quantity">Quantity</label>

                    <input
                      id="quantity"
                      type="number"
                      name="quantity"
                      min="0"
                      step="1"
                      value={form.quantity}
                      placeholder="0"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="minQuantity">Minimum Quantity</label>

                    <input
                      id="minQuantity"
                      type="number"
                      name="minQuantity"
                      min="0"
                      step="1"
                      value={form.minQuantity}
                      placeholder="0"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="new-product-field">
                    <label htmlFor="maxQuantity">Maximum Quantity</label>

                    <input
                      id="maxQuantity"
                      type="number"
                      name="maxQuantity"
                      min="0"
                      step="1"
                      value={form.maxQuantity}
                      placeholder="0"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </section>

              <div className="new-product-actions">
                <button
                  type="button"
                  className="new-product-btn ghost"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="new-product-btn primary"
                  disabled={submitting}
                >
                  {submitting ? "Creating Product..." : "Create Product"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default NewProduct;
